import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { IDEA_STATUS_LABELS, IDEA_STATUS_VALUES } from '@cult/domain';
import type { PublicIdea } from '@cult/schemas';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Button, Flex, Input, Segmented, Skeleton, Typography } from 'antd';
import type { ReactNode } from 'react';
import { BlankState, FilterRow, FilterRowSpacer, InlineError } from '../../../../components';
import { useDebouncedValue } from '../../../../lib/use-debounced-value';
import { MUTED_ICON_COLOR } from '../../../../theme/tokens';
import styles from './FeedShell.module.scss';

import { type IdeaFilters, useIdeas } from '../../hooks';
import type { FeedSearch, StatusFilter } from '../../utils';

type Props = {
  renderList: (ideas: PublicIdea[]) => ReactNode;
};

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: '', label: 'All ideas' },
  ...IDEA_STATUS_VALUES.map((status) => ({ value: status, label: IDEA_STATUS_LABELS[status] })),
];

export const FeedShell = ({ renderList }: Props) => {
  const search = useSearch({ from: '/_auth/_feed/ideas' });
  const navigate = useNavigate();

  const { q: query, status: statusFilter } = search;

  const patch = (updates: Partial<FeedSearch>) => {
    navigate({
      to: '/ideas',
      search: (prev) => ({ ...prev, ...updates }),
      replace: true,
    });
  };

  // Search only fires at 3+ chars; shorter input counts as no search.
  const debouncedQuery = useDebouncedValue(query.trim(), 300);
  const searchTerm = debouncedQuery.length >= 3 ? debouncedQuery : '';

  // The feed is inherently private — the API scopes it to the current member's own ideas.
  const filters: IdeaFilters = {};
  if (searchTerm) filters.q = searchTerm;
  if (statusFilter) filters.status = statusFilter;

  const ideasQuery = useIdeas(filters);
  const all = ideasQuery.data?.pages.flatMap((p) => p.ideas) ?? [];

  const searchActive = searchTerm.length > 0;

  return (
    <Flex vertical gap="large">
      <Typography.Title level={3} style={{ margin: 0 }}>
        Ideas
      </Typography.Title>

      <Flex vertical gap="middle" className={styles.filters}>
        <FilterRow>
          <Segmented<StatusFilter>
            value={statusFilter}
            onChange={(v) => patch({ status: v })}
            size="large"
            options={STATUS_OPTIONS}
          />
          <FilterRowSpacer />
          <Input
            allowClear
            prefix={<SearchOutlined style={{ color: MUTED_ICON_COLOR }} />}
            placeholder="Search ideas"
            value={query}
            onChange={(e) => patch({ q: e.target.value })}
            style={{ width: 260 }}
          />
          <Link to="/submit">
            <Button type="primary" icon={<PlusOutlined />} style={{ width: 140 }}>
              Submit idea
            </Button>
          </Link>
        </FilterRow>
      </Flex>

      {ideasQuery.isError ? <InlineError title="Couldn't load ideas" detail={ideasQuery.error.message} /> : null}

      {ideasQuery.isPending && !ideasQuery.isPlaceholderData ? (
        <Flex vertical gap="middle">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} active paragraph={{ rows: 2 }} />
          ))}
        </Flex>
      ) : ideasQuery.isError && all.length === 0 ? null : searchActive && all.length === 0 ? (
        <BlankState
          title={`No matches for "${searchTerm}"`}
          hint="Try a shorter query or clear the search."
          action={<Button onClick={() => patch({ q: '' })}>Clear search</Button>}
        />
      ) : !searchActive && all.length === 0 ? (
        <BlankState
          title="No ideas yet"
          hint="Submit your first idea to get started."
          action={
            <Link to="/submit">
              <Button type="primary">Submit idea</Button>
            </Link>
          }
        />
      ) : (
        <div style={{ animation: 'fade-in var(--dur-fast) var(--ease-out) both' }}>
          {renderList(all)}
          {ideasQuery.hasNextPage ? (
            <div style={{ textAlign: 'center', paddingTop: 8 }}>
              <Button onClick={() => ideasQuery.fetchNextPage()} loading={ideasQuery.isFetchingNextPage}>
                Load more
              </Button>
            </div>
          ) : null}
        </div>
      )}
    </Flex>
  );
};
